import { FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';

import { Bundle, DEFAULT_CURRENCY_CODE, DEFAULT_PHONE_NUMBER_FORMAT, withDefaultBundleSettings } from '../Models/Bundle';

type AppSettingsPropertiesProps = {
	bundle: Bundle;
	onBundleChanged: (bundle: Bundle) => void;
};

const PHONE_NUMBER_FORMAT_OPTIONS = [
	{ label: 'United States', value: '(###) ###-####' },
	{ label: 'Germany', value: '+49 ### ########' },
	{ label: 'Poland', value: '+48 ### ### ###' },
	{ label: 'United Kingdom', value: '+44 #### ######' },
	{ label: 'France', value: '+33 # ## ## ## ##' },
	{ label: 'International', value: '+## ### ### ####' },
];

const CURRENCY_CODE_OPTIONS = [
	{ label: 'United States Dollar', value: 'USD' },
	{ label: 'Euro', value: 'EUR' },
	{ label: 'Polish Zloty', value: 'PLN' },
	{ label: 'British Pound', value: 'GBP' },
	{ label: 'Canadian Dollar', value: 'CAD' },
	{ label: 'Swiss Franc', value: 'CHF' },
];

export function AppSettingsProperties({ bundle, onBundleChanged }: AppSettingsPropertiesProps) {
	const bundleWithSettings = withDefaultBundleSettings(bundle);
	const phoneNumberFormat = bundleWithSettings.Settings?.PhoneNumberFormat ?? DEFAULT_PHONE_NUMBER_FORMAT;
	const currencyCode = bundleWithSettings.Settings?.CurrencyCode ?? DEFAULT_CURRENCY_CODE;
	const selectedPhoneNumberOption = PHONE_NUMBER_FORMAT_OPTIONS.some((option) => option.value === phoneNumberFormat) ? phoneNumberFormat : '';
	const selectedCurrencyOption = CURRENCY_CODE_OPTIONS.some((option) => option.value === currencyCode) ? currencyCode : '';

	const handleSettingsChanged = (settings: NonNullable<Bundle['Settings']>) => {
		onBundleChanged({
			...bundleWithSettings,
			Settings: {
				...bundleWithSettings.Settings,
				...settings,
			},
		});
	};

	const handlePhoneNumberFormatChanged = (format: string) => {
		handleSettingsChanged({ PhoneNumberFormat: format || DEFAULT_PHONE_NUMBER_FORMAT });
	};

	const handleCurrencyCodeChanged = (code: string) => {
		handleSettingsChanged({ CurrencyCode: code.toUpperCase() || DEFAULT_CURRENCY_CODE });
	};

	return (
		<Stack spacing={2}>
            <FormControl size="small" fullWidth>
                <InputLabel id="anonymous-identity-storage-label">Anonymous visitor identity</InputLabel>
                <Select labelId="anonymous-identity-storage-label" label="Anonymous visitor identity"
                    value={bundleWithSettings.Settings?.AnonymousIdentityStorage ?? 'SessionStorage'}
                    onChange={(event) => handleSettingsChanged({ AnonymousIdentityStorage: event.target.value as NonNullable<Bundle['Settings']>['AnonymousIdentityStorage'] })}>
                    <MenuItem value="SessionStorage">Tab session (default)</MenuItem>
                    <MenuItem value="LocalStorage">Remember in browser</MenuItem>
                    <MenuItem value="Memory">Memory only</MenuItem>
                </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">Controls anonymous App Embed identity storage. Tab session survives reloads in the same tab; browser storage survives browser restarts; memory only lasts until the widget is destroyed. Server identity expiry still applies. Save and deploy the App for visitors to use this setting.</Typography>
            {(['AllowFileReads', 'AllowFileUploads'] as const).map((setting) => {
                const label = setting === 'AllowFileReads' ? 'File reads' : 'File uploads';
                const value = bundleWithSettings.Settings?.[setting];
                return <FormControl size="small" fullWidth key={setting}>
                    <InputLabel>{label}</InputLabel>
                    <Select label={label} value={typeof value === 'boolean' ? (value ? 'allow' : 'deny') : 'default'}
                        onChange={(event) => handleSettingsChanged({ [setting]: event.target.value === 'default' ? undefined : event.target.value === 'allow' })}>
                        <MenuItem value="default">Default</MenuItem>
                        <MenuItem value="allow">Allow</MenuItem>
                        <MenuItem value="deny">Deny</MenuItem>
                    </Select>
                </FormControl>;
            })}
            <Typography variant="caption" color="text.secondary">Default blocks file access in embeds and keeps existing authenticated App behavior. Chats can restrict these permissions further.</Typography>
			<FormControl size="small" fullWidth>
				<InputLabel>Phone Number Preset</InputLabel>
				<Select label="Phone Number Preset" value={selectedPhoneNumberOption} onChange={(e) => handlePhoneNumberFormatChanged(e.target.value)}>
					<MenuItem value="">
						<em>Custom</em>
					</MenuItem>
					{PHONE_NUMBER_FORMAT_OPTIONS.map((option) => (
						<MenuItem key={option.label} value={option.value}>
							{option.label} - {option.value}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<TextField
				label="Phone Number Format"
				value={phoneNumberFormat}
				onChange={(e) => handlePhoneNumberFormatChanged(e.target.value)}
				size="small"
				fullWidth
				helperText="# is replaced with each digit, for example (###) ###-####."
			/>
			<FormControl size="small" fullWidth>
				<InputLabel>Currency Preset</InputLabel>
				<Select label="Currency Preset" value={selectedCurrencyOption} onChange={(e) => handleCurrencyCodeChanged(e.target.value)}>
					<MenuItem value="">
						<em>Custom</em>
					</MenuItem>
					{CURRENCY_CODE_OPTIONS.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label} - {option.value}
						</MenuItem>
					))}
				</Select>
			</FormControl>
			<TextField
				label="Currency Code"
				value={currencyCode}
				onChange={(e) => handleCurrencyCodeChanged(e.target.value)}
				size="small"
				fullWidth
				helperText="Default ISO currency code for currency-formatted Data Display properties."
			/>
		</Stack>
	);
}
